import { useMemo } from 'react';
import { verifiers, constants } from '@yola/subscription-manager-js';
import configStorage from '../../configuration/config-storage';
import getPriceData from '../../common/helpers/get-price-data';
import { PER_YEAR, API_TERM_VALUES } from '../../common/constants/periods';
import { t } from '../../localization';
import getPlanLabel from '../helpers/get-plan-label';
import { getPlanDescription } from '../helpers/get-captions';

const {
  SubscriptionType: { B2C_HOSTING_GOLD },
} = constants;

const usePlansData = ({
  currentPlan,
  currentTerm,
  prices,
  currency,
  minRequiredPlan,
  paymentPeriod,
  availablePlansData,
  areEcommercePlans,
  isFreePlanAvailable,
  isLimitedOfferAvailable,
  isIntroDiscountAvailable,
  captions,
  onPlanSelect,
}) => {
  const { locale = 'en' } = configStorage.get();

  return useMemo(() => {
    const { planIds: supportedPlanList } = availablePlansData;
    const preparedPlans = supportedPlanList.map((plan) => {
      let available = false;

      if (verifiers.isFreeHostingPackageType(plan) && isFreePlanAvailable) {
        available = true;
      } else {
        available = verifiers.isPlanAvailable({
          plan,
          currentPlan,
          minRequiredPlan,
          term: API_TERM_VALUES[paymentPeriod],
          currentTerm,
        });
      }

      return {
        name: availablePlansData.map[plan].name,
        mostPopular: Boolean(availablePlansData.map[plan].meta.mostPopular),
        type: plan,
        id: plan,
        price: getPriceData({ prices, plan, currency, locale, isIntroDiscountAvailable }),
        current: plan === currentPlan,
        available,
      };
    });

    const mostPopularPlanIndex = preparedPlans.findIndex((plan) => plan.mostPopular);
    const mostValuablePlanIndex =
      mostPopularPlanIndex < 0
        ? mostPopularPlanIndex
        : preparedPlans.findIndex((plan, index) => plan.available && index >= mostPopularPlanIndex);

    const isFreePackageType = areEcommercePlans
      ? verifiers.isFreeEcommercePlanType
      : verifiers.isFreeHostingPackageType;

    return preparedPlans.map((plan, index) => {
      const { type, available, price, mostPopular, name, id, current } = plan;
      const isMostValuable = mostValuablePlanIndex === index;
      const isLimitedOffer = isLimitedOfferAvailable && !current && type === B2C_HOSTING_GOLD;

      let cta = captions?.plan?.buttonLabel || t('Select');

      if (verifiers.isFreeHostingPackageType(plan.type) && isFreePlanAvailable) {
        cta = t('Continue for Free');
      }

      const pricePartsForTargetPeriod =
        paymentPeriod === PER_YEAR ? price.perYear?.parts : price.perMonth?.parts;

      const oldPriceForTargetPeriod =
        paymentPeriod === PER_YEAR
          ? (price?.perYear?.oldPrice?.month ?? 0)
          : (price?.perMonth?.oldPrice?.month ?? 0);

      return {
        captions: {
          pricing: {
            first: `${pricePartsForTargetPeriod?.month.currency}${pricePartsForTargetPeriod?.month.integer}`,
            second: pricePartsForTargetPeriod?.month.fraction,
            period: t('/ Month'),
            ...(isIntroDiscountAvailable &&
              oldPriceForTargetPeriod && { oldPrice: oldPriceForTargetPeriod }),
          },
          name,
          description: getPlanDescription(type),
          cta,
          label: getPlanLabel(type, currentPlan, mostPopular, isMostValuable, isLimitedOffer),
          ...(isLimitedOffer && {
            tagLabel: t('Get a website built for you'),
          }),
          ...(isIntroDiscountAvailable && {
            introDiscountLabel: `70% off 1<sup class="ws-ui-plan-discount__sup-text">st</sup> billing cycle`, // It was decided not to localize this string for now
          }),
          ...(paymentPeriod === PER_YEAR && {
            priceDescription: !isFreePackageType(id) ? t('billed annually') : '',
          }),
        },
        disabled: !available,
        highlighted: isMostValuable,
        limited: isLimitedOffer,
        type,
        id,
        onPlanSelect,
      };
    });
  }, [
    availablePlansData,
    areEcommercePlans,
    isFreePlanAvailable,
    prices,
    currency,
    locale,
    isIntroDiscountAvailable,
    currentPlan,
    minRequiredPlan,
    paymentPeriod,
    currentTerm,
    isLimitedOfferAvailable,
    captions?.plan?.buttonLabel,
    onPlanSelect,
  ]);
};

export default usePlansData;
